import { useState } from "react";
import { useNotify } from "ra-core";
import { Link } from "react-router";
import {
  ShieldCheck,
  FileJson,
  Cookie,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { openCookiePreferences } from "./CookieConsentBanner";
import { COMPANY_LEGAL_CONFIG } from "./legalConfig";

export const UserPrivacyRightsCard = () => {
  const notify = useNotify();
  const [exporting, setExporting] = useState(false);

  const handleExportSelfData = async () => {
    setExporting(true);
    try {
      const client = getSupabaseClient();
      const { data: sessionData } = await client.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        notify("Sessão expirada. Faça login novamente.", { type: "error" });
        return;
      }

      const res = await fetch("/api/lgpd?action=export_self", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(
          json?.error?.message || "Erro ao exportar seus dados pessoais.",
        );
      }

      const blob = new Blob([JSON.stringify(json.data, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meus-dados-lgpd-dotus-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      notify(
        "Seus dados cadastrais e histórico de consentimentos foram exportados em JSON.",
        { type: "success" },
      );
    } catch (err: any) {
      notify(err?.message || "Erro ao exportar dados.", { type: "error" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 shrink-0">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-muted-foreground">
                Privacidade & Meus Direitos LGPD (Art. 18)
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Em conformidade com a Lei Geral de Proteção de Dados (Lei nº
                13.709/2018), você pode exportar seus dados cadastrais e
                histórico de consentimentos em formato estruturado (JSON),
                gerenciar preferências de cookies ou acionar o Encarregado de
                Proteção de Dados (DPO).
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={exporting}
              onClick={handleExportSelfData}
              className="text-xs gap-2 cursor-pointer"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileJson className="w-4 h-4 text-primary" />
              )}
              <span>Exportar Meus Dados (JSON)</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openCookiePreferences}
              className="text-xs gap-2 cursor-pointer"
            >
              <Cookie className="w-4 h-4 text-primary" />
              <span>Preferências de Cookies</span>
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              asChild
              className="text-xs gap-1.5"
            >
              <Link to="/privacidade">
                <span>Política de Privacidade</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </Button>
          </div>

          <div className="rounded-lg bg-muted/40 border border-border/60 p-3 text-[11px] text-muted-foreground leading-relaxed">
            <strong>Canal do Encarregado (DPO - Art. 41 LGPD):</strong> Para
            solicitar eliminação/anonimização de conta, oposição a tratamento ou
            esclarecimentos adicionais, envie um e-mail para{" "}
            <code className="text-foreground font-semibold">
              {COMPANY_LEGAL_CONFIG.dpoEmail}
            </code>{" "}
            ({COMPANY_LEGAL_CONFIG.dpoName}).
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

